import { ElementType } from 'react';

// MUI
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import useResponsive from 'hooks/useResponsive';
import CustomTooltip from 'components/CustomTooltip';

interface TooltipContent {
  text: string;
  link?: {
    text: string;
    href: string;
  };
}

interface ActionInfoButtonProps {
  Icon: ElementType;
  message: string;
  tooltipContent?: TooltipContent;
  marginTop?: number;
  dataTestId?: string;
}

// Styles
const styles = {
  icon: {
    color: '#497262',
    marginRight: 0.5,
  },
  infoButton: {
    padding: 0,
    marginLeft: 0.25,
    color: '#0000008F',
  },
  tooltipLink: {
    color: '#ffffff',
    textDecoration: 'underline',
  },
} as const;

// Components
const TooltipContentRenderer = ({ content }: { content: TooltipContent }) => {
  const linkHtml = content.link
    ? `<a href="${content.link.href}" target="_blank" rel="noopener" style="color: #ffffff; text-decoration: underline;">${content.link.text}</a>`
    : '';

  return (
    <Typography
      dangerouslySetInnerHTML={{
        __html: `${content.text} ${linkHtml}`,
      }}
    />
  );
};

const ActionInfoButton = ({ Icon, message, tooltipContent, marginTop = 0.5, dataTestId }: ActionInfoButtonProps) => {
  const isMobile = useResponsive('down', 'sm');

  const iconStyles = {
    ...styles.icon,
    fontSize: isMobile ? '1rem' : '1.25rem',
  };

  const messageStyles = {
    fontSize: isMobile ? '0.875rem' : '1rem',
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      mt={marginTop}
      data-testid={dataTestId}
      justifyContent={isMobile ? 'space-between' : 'flex-start'}
    >
      <Stack direction="row" alignItems="center">
        <Icon sx={iconStyles} />
        <Typography dangerouslySetInnerHTML={{ __html: message }} sx={messageStyles} />
      </Stack>

      {tooltipContent && <CustomTooltip title={<TooltipContentRenderer content={tooltipContent} />} />}
    </Stack>
  );
};

export default ActionInfoButton;
